/**
 * PBR-Materialien aus der Bibliothek. Texturen aus dem Admin (Farbe/Normal/Roughness) haben Vorrang,
 * sonst erzeugen wir eine prozedurale Ersatztextur (Holzmaserung, Leinwandgewebe, gebürstetes Metall …).
 * Alle Geometrien des Renderers haben UVs in METERN – die Kachelgröße `tileCm` wird hier zur Wiederholung.
 */
import * as THREE from 'three'
import type { LibraryMaterial, Preview3dLibrary, ProceduralPattern } from './types.ts'

const textureCache = new Map<string, Promise<THREE.Texture | null>>()
const proceduralCache = new Map<string, { map: THREE.Texture | null; normal: THREE.Texture | null; rough: THREE.Texture | null }>()

let maxAnisotropy = 4
export function setMaxAnisotropy(v: number) {
  maxAnisotropy = Math.max(1, Math.min(16, v || 1))
}

function loadTexture(url: string, srgb: boolean): Promise<THREE.Texture | null> {
  const key = `${srgb ? 's' : 'l'}|${url}`
  let p = textureCache.get(key)
  if (!p) {
    p = new Promise((resolve) => {
      const loader = new THREE.TextureLoader()
      loader.setCrossOrigin('anonymous')
      loader.load(
        url,
        (tex: THREE.Texture) => {
          tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
          tex.wrapS = tex.wrapT = THREE.RepeatWrapping
          tex.anisotropy = maxAnisotropy
          resolve(tex)
        },
        undefined,
        () => resolve(null),
      )
    })
    textureCache.set(key, p)
  }
  return p
}

/** Deterministisches Rauschen (kein Math.random, damit Vorschau stabil bleibt) */
function hash(n: number) {
  const x = Math.sin(n * 127.1) * 43758.5453
  return x - Math.floor(x)
}

function canvasTex(c: HTMLCanvasElement, srgb: boolean) {
  const t = new THREE.CanvasTexture(c)
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  t.anisotropy = maxAnisotropy
  return t
}

/** Höhenfeld → Normal-Map */
function heightToNormal(h: Float32Array, w: number, hgt: number, strength: number) {
  const c = document.createElement('canvas')
  c.width = w
  c.height = hgt
  const ctx = c.getContext('2d')!
  const img = ctx.createImageData(w, hgt)
  for (let y = 0; y < hgt; y++) {
    for (let x = 0; x < w; x++) {
      const l = h[y * w + ((x - 1 + w) % w)]!, r = h[y * w + ((x + 1) % w)]!
      const u = h[((y - 1 + hgt) % hgt) * w + x]!, d = h[((y + 1) % hgt) * w + x]!
      let nx = (l - r) * strength, ny = (u - d) * strength, nz = 1
      const len = Math.hypot(nx, ny, nz)
      nx /= len; ny /= len; nz /= len
      const i = (y * w + x) * 4
      img.data[i] = (nx * 0.5 + 0.5) * 255
      img.data[i + 1] = (ny * 0.5 + 0.5) * 255
      img.data[i + 2] = (nz * 0.5 + 0.5) * 255
      img.data[i + 3] = 255
    }
  }
  ctx.putImageData(img, 0, 0)
  return canvasTex(c, false)
}

function procedural(pattern: ProceduralPattern, baseColor: string) {
  const key = `${pattern}|${baseColor}`
  const hit = proceduralCache.get(key)
  if (hit) return hit
  let out: { map: THREE.Texture | null; normal: THREE.Texture | null; rough: THREE.Texture | null } = { map: null, normal: null, rough: null }
  const S = 256
  const base = new THREE.Color(baseColor)

  if (pattern === 'wood') {
    // Maserung entlang U (Leistenrichtung): Jahresringe als verzerrte Sinusbänder
    const c = document.createElement('canvas')
    c.width = 512
    c.height = 128
    const ctx = c.getContext('2d')!
    const img = ctx.createImageData(c.width, c.height)
    const h = new Float32Array(c.width * c.height)
    for (let y = 0; y < c.height; y++) {
      for (let x = 0; x < c.width; x++) {
        const warp = Math.sin(x * 0.013 + hash(Math.floor(y / 9)) * 6) * 6 + Math.sin(x * 0.051) * 1.5
        const ring = Math.sin((y + warp) * 0.42 + hash(Math.floor(x / 64)) * 0.6)
        const fine = (hash(x * 0.37 + y * 913.1) - 0.5) * 0.12
        const v = 0.5 + ring * 0.22 + fine
        h[y * c.width + x] = v
        const k = 0.78 + v * 0.34
        const i = (y * c.width + x) * 4
        img.data[i] = Math.min(255, base.r * 255 * k)
        img.data[i + 1] = Math.min(255, base.g * 255 * k * 0.98)
        img.data[i + 2] = Math.min(255, base.b * 255 * k * 0.95)
        img.data[i + 3] = 255
      }
    }
    ctx.putImageData(img, 0, 0)
    out = { map: canvasTex(c, true), normal: heightToNormal(h, c.width, c.height, 1.2), rough: null }
  } else if (pattern === 'weave') {
    // Leinwandgewebe: Kette/Schuss als Höhenfeld
    const n = 64
    const h = new Float32Array(n * n)
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const wx = Math.sin((x / n) * Math.PI * 16), wy = Math.sin((y / n) * Math.PI * 16)
        const over = (Math.floor(x / 4) + Math.floor(y / 4)) % 2 === 0
        h[y * n + x] = (over ? Math.abs(wy) : Math.abs(wx)) * 0.8 + hash(x * 31 + y * 17) * 0.2
      }
    }
    out = { map: null, normal: heightToNormal(h, n, n, 2.2), rough: null }
  } else if (pattern === 'brushed') {
    const c = document.createElement('canvas')
    c.width = S
    c.height = S
    const ctx = c.getContext('2d')!
    const h = new Float32Array(S * S)
    const img = ctx.createImageData(S, S)
    for (let y = 0; y < S; y++) {
      const line = hash(y * 7.3) * 0.5 + hash(Math.floor(y / 3) * 1.7) * 0.5
      for (let x = 0; x < S; x++) {
        const v = line * 0.85 + hash(x * 0.11 + y * 57.3) * 0.15
        h[y * S + x] = v
        const g = 200 + v * 40
        const i = (y * S + x) * 4
        img.data[i] = img.data[i + 1] = img.data[i + 2] = g
        img.data[i + 3] = 255
      }
    }
    ctx.putImageData(img, 0, 0)
    out = { map: null, normal: heightToNormal(h, S, S, 0.6), rough: canvasTex(c, false) }
  } else if (pattern === 'grain' || pattern === 'paper') {
    const n = 128
    const h = new Float32Array(n * n)
    for (let i = 0; i < n * n; i++) h[i] = hash(i * 0.731) * (pattern === 'paper' ? 0.5 : 0.25)
    out = { map: null, normal: heightToNormal(h, n, n, pattern === 'paper' ? 0.8 : 0.4), rough: null }
  }
  proceduralCache.set(key, out)
  return out
}

function repeatFor(tex: THREE.Texture, tileCm: number, rotate = false) {
  const t = tex.clone()
  t.needsUpdate = true
  const r = 100 / Math.max(0.5, tileCm || 20)
  t.repeat.set(r, r)
  if (rotate) t.rotation = Math.PI / 2
  return t
}

export type MaterialOptions = {
  /** Holzmaserung quer laufen lassen (z. B. Keilrahmen-Stirnseite) */
  rotate?: boolean
  /** Bildtextur (Druck) – überschreibt Farbe */
  printMap?: THREE.Texture | null
  /** Farbe überschreiben (z. B. Kantenfarbe) */
  color?: string
}

/**
 * Material aus der Bibliothek bauen. Texturdateien werden nachgeladen und dann eingesetzt
 * (`onUpdate` → neu rendern); bis dahin gilt die prozedurale Ersatztextur.
 */
export function buildMaterial(lib: Preview3dLibrary, slug: string, opts: MaterialOptions = {}, onUpdate?: () => void): THREE.MeshPhysicalMaterial {
  const m = lib.materials.find((x) => x.slug === slug) ?? lib.materials.find((x) => x.slug === 'lack-weiss')!
  return materialFrom(m, opts, onUpdate)
}

export function materialFrom(m: LibraryMaterial, opts: MaterialOptions = {}, onUpdate?: () => void): THREE.MeshPhysicalMaterial {
  const mat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(opts.printMap ? '#ffffff' : (opts.color || m.color)),
    roughness: m.roughness,
    metalness: m.metalness,
    clearcoat: m.clearcoat,
    clearcoatRoughness: m.clearcoatRoughness,
    sheen: m.sheen,
    ior: m.ior,
  })
  if (m.transmission > 0) {
    mat.transmission = m.transmission
    mat.thickness = 0.005
    mat.attenuationColor = new THREE.Color(m.attenuationColor || '#ffffff')
    mat.attenuationDistance = 0.08
    mat.transparent = false
  }
  if (opts.printMap) mat.map = opts.printMap

  const tint = opts.color || m.color
  const proc = procedural(m.procedural, tint)
  if (!opts.printMap && proc.map) {
    // die Ersatztextur ist schon eingefärbt – sonst würde doppelt getönt
    mat.map = repeatFor(proc.map, m.tileCm, opts.rotate)
    mat.color.set('#ffffff')
  }
  if (proc.normal) {
    mat.normalMap = repeatFor(proc.normal, m.tileCm, opts.rotate)
    mat.normalScale.set(m.normalScale, m.normalScale)
  }
  if (proc.rough) mat.roughnessMap = repeatFor(proc.rough, m.tileCm, opts.rotate)

  const swap = (url: string | null, srgb: boolean, apply: (t: THREE.Texture) => void) => {
    if (!url) return
    void loadTexture(url, srgb).then((t) => {
      if (!t) return
      apply(repeatFor(t, m.tileCm, opts.rotate))
      mat.needsUpdate = true
      onUpdate?.()
    })
  }
  // Farbtextur: Grundfarbe bleibt als Tönung erhalten (weiß = Textur unverändert)
  if (!opts.printMap) swap(m.colorMapUrl, true, (t) => { mat.map = t; mat.color.set(tint) })
  swap(m.normalMapUrl, false, (t) => { mat.normalMap = t; mat.normalScale.set(m.normalScale, m.normalScale) })
  swap(m.roughnessMapUrl, false, (t) => { mat.roughnessMap = t })
  return mat
}

/** Oberfläche des Drucks auf das Frontmaterial anwenden */
export function applySurface(mat: THREE.MeshPhysicalMaterial, surface: 'matt' | 'satin' | 'gloss') {
  if (surface === 'gloss') {
    mat.roughness = Math.min(mat.roughness, 0.18)
    mat.clearcoat = Math.max(mat.clearcoat, 0.7)
    mat.clearcoatRoughness = 0.05
  } else if (surface === 'satin') {
    mat.roughness = Math.min(mat.roughness, 0.45)
    mat.clearcoat = Math.max(mat.clearcoat, 0.25)
    mat.clearcoatRoughness = 0.3
  }
}
